import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { CARD_CONFIG } from '../../config/cardConfig';

// Updated symbols with emojis to match the new probability table
export const symbolIcons = {
    // Regular symbols
    Cherry: { emoji: '🍒', color: '#FF5252', type: 'Common', payout: 1.0 },      // 1x base
    Lemon: { emoji: '🍋', color: '#FFEB3B', type: 'Common', payout: 2.0 },       // 2x base
    Apple: { emoji: '🍎', color: '#F44336', type: 'Common', payout: 3.0 },       // 3x base
    Blueberry: { emoji: '🫐', color: '#3F51B5', type: 'Common', payout: 4.0 },   // 4x base
    Banana: { emoji: '🍌', color: '#FFEB3B', type: 'Uncommon', payout: 5.0 },    // 5x base
    Watermelon: { emoji: '🍉', color: '#4CAF50', type: 'Uncommon', payout: 10.0 },// 10x base
    Orange: { emoji: '🍊', color: '#FF9800', type: 'Rare', payout: 15.0 },       // 15x base
    Grape: { emoji: '🍇', color: '#9C27B0', type: 'Rare', payout: 25.0 },        // 25x base
    GreenApple: { emoji: '🍏', color: '#8BC34A', type: 'Epic', payout: 40.0 },   // 40x base
    Aubergine: { emoji: '🍆', color: '#673AB7', type: 'Epic', payout: 75.0 },    // 75x base
    Crown: { emoji: '👑', color: '#FFC107', type: 'Epic', payout: 125.0 },       // 125x base
    Mushroom: { emoji: '🍄', color: '#F44336', type: 'Epic', payout: 250.0 },    // 250x base
    Star: { emoji: '⭐', color: '#FFC107', type: 'Legendary', payout: 500.0 },    // 500x base
    Bell: { emoji: '🔔', color: '#FFD700', type: 'Legendary', payout: 1000.0 },  // 1000x base
    Clove: { emoji: '🍀', color: '#4CAF50', type: 'Mythic', payout: 5000.0 },    // 5000x base
    Peppers: { emoji: '🌶️', color: '#F44336', type: 'Mythic', payout: 10000.0 }, // 10000x base
    
    // Special bonus symbols
    Ticket: { emoji: '🎟️', color: '#4CAF50', type: 'Bonus', payout: 0 },         // Lottery ticket
    Chest: { emoji: '🎁', color: '#FFD700', type: 'Bonus', payout: 0 }           // Bonus game (5x-100x)
};

const Card = ({ id, isFlipped, onFlip, disabled, symbol, isMatched, type, position, vrfProof }) => {
    const symbolConfig = symbol ? symbolIcons[symbol] : null;
    
    // Get card background color based on type
    const getCardColor = () => {
        switch (type) {
            case CARD_CONFIG.TYPES.LEGENDARY:
                return 'linear-gradient(135deg, #FFD700, #FFA500)';
            case CARD_CONFIG.TYPES.EPIC:
                return 'linear-gradient(135deg, #9400D3, #4B0082)';
            case CARD_CONFIG.TYPES.RARE:
                return 'linear-gradient(135deg, #4169E1, #0000CD)';
            default:
                return 'rgba(88, 65, 140, 0.9)';
        }
    };
    
    return (
        <Box
            position={position ? "absolute" : "relative"}
            left={position ? position.x : undefined}
            top={position ? position.y : undefined}
            w={position ? CARD_CONFIG.CARD_WIDTH : "100%"}
            h={position ? CARD_CONFIG.CARD_HEIGHT : "100%"}
            cursor={disabled ? 'default' : 'pointer'}
            onClick={() => !disabled && onFlip(id)}
            style={{ perspective: '1000px' }}
        >
            <Box
                position="relative"
                width="100%"
                height="100%"
                transition="transform 0.6s"
                style={{
                    transformStyle: 'preserve-3d',
                    transform: `rotateY(${isFlipped ? '180deg' : '0deg'})`
                }}
            >
                {/* Front face - Purple with logo */}
                <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    bg="rgba(128, 90, 213, 0.9)"
                    borderRadius="lg"
                    border="2px solid"
                    borderColor="purple.400"
                    boxShadow="0 8px 16px rgba(0, 0, 0, 0.3)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{
                        transform: disabled ? 'none' : 'scale(1.02)',
                        boxShadow: disabled ? '0 8px 16px rgba(0, 0, 0, 0.3)' : '0 8px 20px rgba(128, 90, 213, 0.5)'
                    }}
                    transition="all 0.2s"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(0deg)'
                    }}
                >
                    <Box
                        as="img"
                        src="/images/palm-heist-logo.png"
                        alt="Palm Heist Logo"
                        w="85%"
                        h="auto"
                        objectFit="contain"
                        filter="drop-shadow(0px 0px 5px rgba(255, 255, 255, 0.2))"
                        opacity="0.9"
                    />
                </Box>

                {/* Back face - Purple with symbol */}
                <Box
                    position="absolute"
                    width="100%"
                    height="100%"
                    bg={getCardColor()}
                    borderRadius="lg"
                    border="2px solid"
                    borderColor="purple.500"
                    boxShadow={isMatched ? '0 0 10px 3px rgba(255, 215, 0, 0.5)' : '0 8px 16px rgba(0, 0, 0, 0.3)'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                    }}
                >
                    {symbolConfig && (
                        <Text
                            fontSize="6xl"
                            fontWeight="bold"
                            color={symbolConfig.color}
                            textShadow="0 0 5px rgba(0, 0, 0, 0.5)"
                            transform="scale(1.3)"
                        >
                            {symbolConfig.emoji}
                        </Text>
                    )}
                    
                    {/* Display card type if no symbol */}
                    {!symbolConfig && (
                        <Text
                            fontSize="xl"
                            fontWeight="bold"
                            color="white"
                            textShadow="0 0 5px rgba(0, 0, 0, 0.5)"
                        >
                            {type}
                        </Text>
                    )}
                    
                    {/* Display VRF value */}
                    {vrfProof && (
                        <Text
                            fontSize="xs"
                            color="white"
                            opacity="0.8"
                            mt={2}
                        >
                            VRF: {vrfProof.toFixed(4)}
                        </Text>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Card;
